<script setup lang="ts">
import { useId } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  disabled?: boolean;
  align?: 'center' | 'start';
}

withDefaults(defineProps<Props>(), { disabled: false, align: 'center' });

const model = defineModel<boolean>({ default: false });
const id = useId();
</script>

<template>
  <label
    :for="id"
    :class="[
      'flex gap-2 text-sm text-foreground select-none',
      align === 'start' ? 'items-start' : 'items-center',
      disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
    ]"
  >
    <span
      :class="[
        'relative size-4 rounded border flex items-center justify-center shrink-0 transition-colors',
        align === 'start' ? 'mt-0.5' : '',
        model ? 'bg-primary border-primary' : 'bg-surface border-border-strong',
      ]"
    >
      <!-- Input is positioned ABSOLUTELY OVER the visible checkmark span
           (rather than the typical sr-only off-label-corner placement) so
           the browser's focus-induced scroll-into-view on label click has
           no visible movement to perform. The earlier sr-only positioning
           pushed the input to the label's top-left, which on long-text
           labels (e.g. SMS consent at the bottom of /schedule Step 5) sat
           hundreds of pixels above the visible checkmark — clicking the
           label scrolled the document to that off-screen position. -->
      <input
        :id="id"
        v-model="model"
        type="checkbox"
        class="absolute inset-0 m-0 opacity-0 cursor-pointer disabled:cursor-default"
        :disabled="disabled"
      />
      <FontAwesomeIcon
        v-if="model"
        :icon="faCheck"
        class="size-2.5 text-white"
        aria-hidden="true"
      />
    </span>
    <slot />
  </label>
</template>
